import { Dispatch, FormEvent, SetStateAction, useEffect, useRef, useState } from "react"
import { GetDocumentModelsSpecification } from "pokal_api"
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks"
import { selectOpenAIKey } from "../../../../redux/AccountSettings"
import { analysisMessageReadySlice, selectReadyMessage } from "../../../../redux/AnalysisMessageReady"

export const OPENAI_MODELS = ["openai:gpt-4o-mini", "openai:gpt-4o"]

export type ChatEntry<TExtras> = {
    role: "user" | "assistant"
    content: string
    extras?: TExtras
}

type StreamEvent = {
    token?: string
    done?: boolean
    error?: boolean | string
    [key: string]: unknown
}

type UseChatStreamOptions<TExtras> = {
    patientId: string
    startStream: (
        userMessage: string,
        history: ChatEntry<TExtras>[],
        model: string
    ) => Promise<ReadableStream>
    parseDone?: (data: StreamEvent, fullText: string) => TExtras | undefined
    errorMessage?: string
    conversationId?: string | null
    initialMessages?: ChatEntry<TExtras>[]
    onUserMessage?: (content: string) => void | Promise<void>
    onAssistantMessage?: (content: string) => void | Promise<void>
}

const DEFAULT_ERROR = "Es tut mir leid, ich kann Ihre Frage momentan nicht beantworten."
const PENDING_GIVE_UP_MS = 300_000

export function useChatStream<TExtras = void>({
    patientId,
    startStream,
    parseDone,
    errorMessage = DEFAULT_ERROR,
    conversationId,
    initialMessages,
    onUserMessage,
    onAssistantMessage,
}: UseChatStreamOptions<TExtras>) {
    const [messages, setMessages] = useState<ChatEntry<TExtras>[]>(initialMessages ?? [])
    const [input, setInput] = useState("")
    const [loading, setLoading] = useState(false)
    const [streaming, setStreaming] = useState("")
    const [ollamaModels, setOllamaModels] = useState<string[]>([])
    const [selectedModel, setSelectedModel] = useState("")
    // tracks the "give up waiting for a pending answer" timer so it can be cleared early
    const giveUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const dispatch = useAppDispatch()
    // set once the backend finishes generating this conversation's answer (pushed via socket)
    const readyMessage = useAppSelector(state => selectReadyMessage(state, conversationId))
    const openaiKey = useAppSelector(selectOpenAIKey)
    const availableModels =
        openaiKey !== null ? [...ollamaModels, ...OPENAI_MODELS] : ollamaModels

    useEffect(() => {
        setMessages([])
        setInput("")
        setStreaming("")
    }, [patientId])

    useEffect(() => {
        setMessages(initialMessages ?? [])
        setInput("")
        setStreaming("")

        if (giveUpTimeoutRef.current) {
            clearTimeout(giveUpTimeoutRef.current)
            giveUpTimeoutRef.current = null
        }

        // history loaded with an unanswered question at the end means generation is likely
        // still running server-side (e.g. after a refresh) - show "..." until it resolves
        const last = initialMessages?.[initialMessages.length - 1]
        const isPending = !!last && last.role === "user"
        setLoading(isPending)

        if (isPending) {
            // fallback in case generation failed before producing anything, so nothing was ever
            // persisted/pushed - without this "..." would hang forever
            giveUpTimeoutRef.current = setTimeout(() => {
                giveUpTimeoutRef.current = null
                setLoading(false)
                setMessages(prev => [...prev, { role: "assistant", content: errorMessage }])
            }, PENDING_GIVE_UP_MS)
        }

        return () => {
            if (giveUpTimeoutRef.current) {
                clearTimeout(giveUpTimeoutRef.current)
                giveUpTimeoutRef.current = null
            }
        }
    }, [conversationId])

    useEffect(() => {
        if (!readyMessage || !conversationId) return
        dispatch(analysisMessageReadySlice.actions.messageConsumed({ conversationId }))
        // Every completed generation fires this event, including for the tab that sent the
        // message and already appended the answer itself via its own live SSE read below.
        // Only apply it here if we're still marked pending (i.e. recovering after a refresh) -
        // otherwise this would duplicate the answer in the sending tab.
        if (!loading) return
        setMessages(prev => [...prev, { role: "assistant", content: readyMessage.content }])
        setLoading(false)
        if (giveUpTimeoutRef.current) {
            clearTimeout(giveUpTimeoutRef.current)
            giveUpTimeoutRef.current = null
        }
    }, [readyMessage, conversationId, loading])

    useEffect(() => {
        GetDocumentModelsSpecification.request({})
            .then(res => {
                const models = (res as { models?: string[] }).models
                if (models?.length) {
                    setOllamaModels(models)
                    setSelectedModel(prev => (prev ? prev : models[0]))
                }
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (openaiKey === null && selectedModel.startsWith("openai:")) {
            setSelectedModel(ollamaModels[0] ?? "")
        }
    }, [openaiKey, ollamaModels, selectedModel])

    const sendMessage = async (message: string, historyBefore: ChatEntry<TExtras>[]) => {
        setLoading(true)
        setStreaming("")
        try {
            const history: ChatEntry<TExtras>[] = [...historyBefore, { role: "user", content: message }]
            const stream = await startStream(message, history, selectedModel)
            const reader = stream.getReader()
            const decoder = new TextDecoder()
            let fullText = ""
            let extras: TExtras | undefined = undefined

            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                const chunk = decoder.decode(value, { stream: true })
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data: ")) continue
                    try {
                        const data = JSON.parse(line.slice(6)) as StreamEvent
                        if (data.token !== undefined) {
                            fullText += data.token
                            setStreaming(fullText)
                        } else if (data.error && data.done) {
                            fullText = errorMessage
                        } else if (data.done) {
                            extras = parseDone?.(data, fullText)
                        }
                    } catch {}
                }
            }

            setStreaming("")
            setMessages(prev => [...prev, { role: "assistant", content: fullText, extras }])
            try { await onAssistantMessage?.(fullText) } catch {}
        } catch {
            setStreaming("")
            setMessages(prev => [...prev, { role: "assistant", content: errorMessage }])
            try { await onAssistantMessage?.(errorMessage) } catch {}
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        const msg = input.trim()
        // selectedModel is empty until /document/models resolves - never send with no
        // model chosen, since that used to silently fall back to a hardcoded model name
        // that may not actually be pulled in Ollama.
        if (!msg || loading || !selectedModel) return
        setInput("")
        const historyBefore = messages
        setMessages(prev => [...prev, { role: "user", content: msg }])
        try { await onUserMessage?.(msg) } catch {}
        await sendMessage(msg, historyBefore)
    }

    return {
        messages,
        streaming,
        loading,
        input,
        setInput: setInput as Dispatch<SetStateAction<string>>,
        handleSubmit,
        availableModels,
        selectedModel,
        setSelectedModel: setSelectedModel as Dispatch<SetStateAction<string>>,
    }
}
